import { c as _c } from "react/compiler-runtime";
import { refreshToken } from "@website/fetcher/refresh-token";
import { LoginStatus, applicationStore, authStore, useSelector } from "@website/store/store";
import { getCookie, removeCookie, setCookie } from "@website/utils/cookie";
import jwtDecode from "jwt-decode";
import { useEffect, useRef } from "react";
import { LEGACY_ACCESS_TOKEN_KEY, LEGACY_REFRESH_TOKEN_KEY, REFRESH_BUFFER_MS, REFRESH_TOKEN_STORAGE_KEY, TOKEN_STORAGE_KEY } from "./constants";
import { deauthorizeUser } from "@metrixorg/websdk";
import { setTracking } from "@website/store/tracking";
import { uuid } from "@website/utils/uuid";
import { captureTokenError, getLegacyTokens, getTokenCookieMaxAgeSeconds, getTokenWithRefresh, isTokenValid, removeTokenFromUrl, updateTokenVersion } from "./helpers";
import { convert } from "./request";
import { DECODED_TOKEN, SetTokenProperties } from "./types";
const CHATBOT_STORAGE_KEY = "chatbot-session";
const tokenHelper = {
  get: (): {
    stToken: string;
    refreshToken: string;
  } => ({
    stToken: getCookie(TOKEN_STORAGE_KEY) || "",
    refreshToken: getCookie(REFRESH_TOKEN_STORAGE_KEY) || ""
  }),
  set: (data: SetTokenProperties): void => {
    const {
      accessToken,
      refreshToken
    } = data;
    const tokenMaxAge = getTokenCookieMaxAgeSeconds(accessToken, REFRESH_BUFFER_MS, "access");
    const refreshTokenMaxAge = refreshToken ? getTokenCookieMaxAgeSeconds(refreshToken, 0, "refresh") : undefined;
    const canPersistAccessToken = isTokenValid(accessToken, tokenMaxAge);
    const canPersistRefreshToken = isTokenValid(refreshToken, refreshTokenMaxAge);
    if (!canPersistAccessToken && !canPersistRefreshToken) {
      authStore.loginStatus.set(LoginStatus.LoggedOut);
      return;
    }
    if (canPersistAccessToken) {
      setCookie(TOKEN_STORAGE_KEY, accessToken, {
        path: "/",
        maxAge: tokenMaxAge
      });
      authStore.loginStatus.set(LoginStatus.Success);
    }
    if (canPersistRefreshToken) {
      setCookie(REFRESH_TOKEN_STORAGE_KEY, refreshToken, {
        path: "/",
        maxAge: refreshTokenMaxAge
      });
    }
    if (canPersistAccessToken || canPersistRefreshToken) {
      updateTokenVersion();
    }
  },
  delete: (): void => {
    removeCookie(TOKEN_STORAGE_KEY, {
      path: "/"
    });
    removeCookie(REFRESH_TOKEN_STORAGE_KEY, {
      path: "/"
    });
    authStore.loginStatus.set(LoginStatus.LoggedOut);
  },
  migrateFromLocalStorage: (data: SetTokenProperties) => {
    tokenHelper.set(data);
    localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
    localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
    authStore.accessToken.delete();
    authStore.refreshToken.delete();
  }
};
export const logout = () => {
  tokenHelper.delete();
  applicationStore.shopping.delete();
  if (typeof window !== "undefined") {
    localStorage.removeItem(CHATBOT_STORAGE_KEY);
    deauthorizeUser();
    authStore.accessToken.delete();
    authStore.refreshToken.delete();
    authStore.tokenVersion.delete();
    localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
    localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
    localStorage.removeItem("auth_store");
    setTracking(previous => ({
      ...previous,
      sid: uuid()
    }));
  }
};
export const saveLogin = (data: SetTokenProperties) => {
  tokenHelper.set(data);
};
export const useJwtToken = (): DECODED_TOKEN => {
  const token = tokenHelper.get().stToken ?? "";
  const fallback: DECODED_TOKEN = {
    analyticsId: "",
    email: "",
    exp: 0,
    iat: 0,
    name: "",
    phoneNumber: "",
    sub: "",
    userPushId: "",
    walletUsername: "",
    customerKey: "",
    profileType: "B2C"
  };
  return (() => {
    if (!token) return fallback;
    try {
      return jwtDecode(token) || fallback;
    } catch {
      captureTokenError("token could  not  be decoded");
      return fallback;
    }
  })();
};
export const useLoginStatus = () => {
  const $ = _c(3);
  const loginStatus = useSelector(authStore.loginStatus);
  const t0 = loginStatus === LoginStatus.Success;
  let t1;
  if ($[0] !== loginStatus || $[1] !== t0) {
    t1 = {
      isLoggedIn: t0,
      loginStatus
    };
    $[0] = loginStatus;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
export const getToken = () => tokenHelper.get();
export const useAuthSessionRefresh = t0 => {
  const $ = _c(11);
  const {
    routeChangeKey,
    queryToken
  } = t0;
  const hasMounted = useRef(false);
  const {
    loginStatus
  } = useLoginStatus();
  const shouldSkipRefresh = loginStatus === LoginStatus.Pending || Boolean(queryToken);
  let t1;
  if ($[0] !== loginStatus || $[1] !== shouldSkipRefresh) {
    t1 = async () => {
      if (shouldSkipRefresh) {
        return;
      }
      const token = await getTokenWithRefresh();
      if (!token.stToken && !token.refreshToken && loginStatus !== LoginStatus.LoggedOut) {
        logout();
      }
    };
    $[0] = loginStatus;
    $[1] = shouldSkipRefresh;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const refreshSession = t1;
  let t2;
  if ($[3] !== refreshSession) {
    t2 = () => {
      if (!hasMounted.current) {
        hasMounted.current = true;
        return;
      }
      refreshSession();
    };
    $[3] = refreshSession;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== routeChangeKey || $[6] !== shouldSkipRefresh) {
    t3 = [routeChangeKey, shouldSkipRefresh];
    $[5] = routeChangeKey;
    $[6] = shouldSkipRefresh;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[8] !== refreshSession) {
    t4 = () => {
      const handleVisibilityChange = () => {
        if (document.visibilityState !== "visible") {
          return;
        }
        refreshSession();
      };
      const handleFocus = () => {
        refreshSession();
      };
      window.addEventListener("focus", handleFocus);
      window.addEventListener("pageshow", handleFocus);
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        window.removeEventListener("focus", handleFocus);
        window.removeEventListener("pageshow", handleFocus);
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      };
    };
    t5 = [refreshSession];
    $[8] = refreshSession;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
};
const refreshAccessToken = async (loginWebengage: () => void) => {
  try {
    await refreshToken();
    loginWebengage();
  } catch {
    authStore.loginStatus.set(LoginStatus.LoggedOut);
  }
};
export const convertToken = async (queryToken: string, loginWebengage: () => void) => {
  const {
    stToken: cookieToken,
    refreshToken: cookieRefreshToken
  } = tokenHelper.get();
  if (cookieToken) {
    loginWebengage();
    authStore.loginStatus.set(LoginStatus.Success);
    updateTokenVersion();
    return;
  }
  if (queryToken) {
    try {
      authStore.loginStatus.set(LoginStatus.Pending);
      await convert(queryToken);
      removeTokenFromUrl();
      loginWebengage();
      return;
    } catch {
      authStore.loginStatus.set(LoginStatus.LoggedOut);
      if (typeof window !== "undefined") {
        captureTokenError("snapp token conversion failed");
      }
      return;
    }
  }
  if (cookieRefreshToken) {
    refreshAccessToken(loginWebengage);
    return;
  }
  if (typeof window !== "undefined") {
    const {
      accessToken: accessFromLocal,
      refreshToken: refreshFromLocal
    } = getLegacyTokens();
    if (accessFromLocal || refreshFromLocal) {
      if (accessFromLocal) {
        tokenHelper.migrateFromLocalStorage({
          accessToken: accessFromLocal,
          refreshToken: refreshFromLocal
        });
        loginWebengage();
        return;
      }
      tokenHelper.migrateFromLocalStorage({
        accessToken: "",
        refreshToken: refreshFromLocal
      });
      refreshAccessToken(loginWebengage);
      return;
    }
  }
  authStore.loginStatus.set(LoginStatus.LoggedOut);
};